import React, { useContext, useState } from "react";
import { Box, IconButton, Menu, MenuButton, MenuItem, MenuList, useToast } from "@chakra-ui/react";
import { FiMoreVertical } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Campaign, Client, Collab } from "@foodfluence/shared";
import { ff } from "theme";
import { conversationApi } from "services/api/conversations";
import { InfluencerDataContext } from "contexts/InfluencerDataContext";
import { getThreadMenuActions } from "../preCollab";

interface ThreadRowMenuProps {
  conversationId: string;
  collab?: Collab;
  campaign?: Campaign;
  client?: Client;
  marketplaceOrderId?: string;
  isArchived: boolean;
}

const openCrisp = () => {
  if (window.$crisp) {
    window.$crisp.push(["do", "chat:show"]);
    window.$crisp.push(["do", "chat:open"]);
  }
};

const itemProps = {
  fontSize: "15px",
  fontWeight: 600,
  color: ff.ink,
  px: "16px",
  minH: "44px",
  _hover: { bg: ff.grey },
  _focus: { bg: ff.grey },
};
const Divider = () => <Box mx="16px" h="1px" bg={ff.line} />;

/**
 * The thread's ⋮ menu on an inbox row. Same items as the open conversation, minus "Send counter",
 * which needs the thread's live spot and slot checks and its counter sheet.
 */
const ThreadRowMenu: React.FC<ThreadRowMenuProps> = ({ conversationId, collab, campaign, client, marketplaceOrderId, isArchived }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { influencerData, isSwitchedAccount } = useContext(InfluencerDataContext);
  const [isArchiving, setIsArchiving] = useState(false);
  const isMarketplace = !!marketplaceOrderId && !collab;
  const actions = getThreadMenuActions({ collab, campaign, isMarketplace, threadExists: true, isArchived });

  const toggleArchive = async () => {
    if (!actions.archive || isArchiving) return;
    const archiving = actions.archive === "archive";
    setIsArchiving(true);
    try {
      await conversationApi.setArchived(conversationId, archiving, isSwitchedAccount ? influencerData?.id : undefined);
      toast({
        title: archiving ? "Chat archived" : "Chat unarchived",
        description: archiving ? "It will come back if the brand replies." : undefined,
        status: "success", duration: 3000, isClosable: true,
      });
    } catch (error) {
      console.error("Error updating archive state:", error);
      toast({ title: "Something went wrong", description: "Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    // The row opens the chat on click and Enter/Space; keep the menu's own events off it.
    <Box flexShrink={0} mt="-10px" mr="-12px" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Menu placement="bottom-end" isLazy>
        <MenuButton
          as={IconButton}
          aria-label={`Options for ${client?.accountName ?? "this chat"}`}
          icon={<FiMoreVertical size={20} />}
          variant="ghost"
          w="44px"
          h="44px"
          minW="44px"
          borderRadius="full"
          color={ff.text2}
          _hover={{ bg: ff.grey, color: ff.ink }}
          _active={{ bg: ff.line }}
        />
        <MenuList
          minWidth="200px"
          border="1px solid"
          borderColor={ff.border}
          borderRadius="16px"
          fontFamily="display"
          boxShadow="0px 4px 24px rgba(0,0,0,0.10), 0px 1px 4px rgba(0,0,0,0.06)"
          py="6px"
          px="0"
          overflow="hidden"
          zIndex={9999}
        >
          {(collab?.id || marketplaceOrderId) && (
            <>
              <MenuItem
                {...itemProps}
                onClick={() =>
                  navigate(collab?.id ? `/collabs/collab-details/${collab.id}` : `/collabs/marketplace-orders/${marketplaceOrderId}/upload`)
                }
              >
                {isMarketplace ? "View marketplace order" : "View collab details"}
              </MenuItem>
              <Divider />
            </>
          )}
          {actions.editProposal && collab && (
            <>
              <MenuItem
                {...itemProps}
                onClick={() =>
                  navigate("/collabs/request-collab", { state: { requestedCollab: collab, campaign, client: { id: client?.id, ...client } } })
                }
              >
                Edit proposal
              </MenuItem>
              <Divider />
            </>
          )}
          {actions.archive && (
            <>
              <MenuItem {...itemProps} onClick={toggleArchive} isDisabled={isArchiving}>
                {actions.archive === "archive" ? "Archive" : "Unarchive"}
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem {...itemProps} onClick={openCrisp}>Need help?</MenuItem>
          <Divider />
          <MenuItem {...itemProps} color={ff.danger} onClick={openCrisp}>Report user</MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default ThreadRowMenu;
